import { onRendered } from '../runtime/legacy.js';

/**
 * AI-plan touches that live outside the legacy core:
 *
 *  - an "Additional notes" field on the plan generator, passed through to the
 *    `generate-plan` edge function;
 *  - an entry point to the generator from the workout history view.
 */

/**
 * The generator form posts a fixed body shape, so the free-text notes are
 * merged in on the way out rather than threaded through the core's form
 * handling. Scoped to `generate-plan` requests; everything else passes straight
 * through untouched.
 */
function interceptGeneratePlan() {
  const originalFetch = window.fetch;

  window.fetch = function ironpactFetch(input, init) {
    const url = typeof input === 'string' ? input : input?.url;
    const notes = document.getElementById('plan-additional-notes')?.value?.trim();

    if (notes && url?.includes('generate-plan') && init?.body) {
      try {
        const body = JSON.parse(init.body);
        body.questionnaire = { ...(body.questionnaire || {}), additional_notes: notes };
        init = { ...init, body: JSON.stringify(body) };
      } catch {
        // Non-JSON body — leave the request exactly as the caller built it.
      }
    }

    return originalFetch.call(this, input, init);
  };
}

function addNotesField(form) {
  // The auth forms are separate views but share the generic form markup; make
  // sure we are on the plan generator before injecting.
  if (form.id === 'login-form' || form.id === 'signup-form') return;
  if (!form.closest('[class*="plan"], [id*="plan"], [class*="generate"], [id*="generate"]')) {
    return;
  }

  const wrap = document.createElement('div');
  wrap.className = 'form-group';
  wrap.innerHTML = `
    <label class="form-label" for="plan-additional-notes">
      Additional notes <span class="form-label-hint">(optional)</span>
    </label>
    <textarea id="plan-additional-notes" class="form-input form-textarea" rows="3"
      placeholder="e.g. I train at home, prefer compound lifts, avoid barbell squats due to knee pain…"></textarea>
  `;

  // No medical disclaimer here: the legacy core already renders one at
  // onboarding and again on the generated plan itself, which is the screen
  // that actually prescribes training (App Store Guideline 1.4.1).
  const submit = form.querySelector('button[type="submit"]');
  if (submit) submit.parentNode.insertBefore(wrap, submit);
  else form.appendChild(wrap);
}

function addHistoryBanner(container) {
  const banner = document.createElement('a');
  banner.href = '#/plans/generate';
  banner.className = 'ai-plans-banner';
  banner.innerHTML = `
    <div class="ai-plans-banner-icon">🤖</div>
    <div class="ai-plans-banner-body">
      <div class="ai-plans-banner-title">Generate an AI Training Plan</div>
      <div class="ai-plans-banner-text">Let AI build a personalised program based on your goals and equipment.</div>
    </div>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="14" height="14"><path d="M9 18l6-6-6-6"/></svg>
  `;
  container.parentNode.insertBefore(banner, container);
}

export function installAIPlans() {
  interceptGeneratePlan();
  onRendered('form', addNotesField);
  onRendered('#history-list-container', addHistoryBanner);
}
